import { MouseEvent, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowUp, ExternalLink, Eye, EyeOff, Heart, Layers, MapPin, MessageCircle, MessageSquare, Play, Quote, TrendingUp, X } from '@digital-twins/design-system/icons'
import { SocialPostItem, SocialPostSource, SocialSentiment, SocialTopicPostItem } from '@digital-twins/shared-types'
import { UserAvatar } from '@/components/ui/UserAvatar'
import { intlLocaleForUi } from '@/i18n'
import { cn } from '@/lib/utils'
import { SocialBrandMark } from './SocialBrandMark'
import { socialMarketCountryLabel, socialMarketReasonText } from './socialMarketFit'
import { SocialPlatformIcon } from './SocialPlatformIcon'
import { SocialReasonTooltip } from './SocialReasonTooltip'
import {
  SOCIAL_PLAY_BUTTON_CLASS,
  SOCIAL_PLAYER_CLOSE_CLASS,
  socialPlayerAspectClass,
  socialPlayerUrl,
  useSocialPlayingPost,
} from './SocialPlayingPostContext'
import { useTopicHighlight } from './SocialTopicHighlightContext'
import { formatSocialDate, SOCIAL_PLATFORM_LABELS, socialMetricLabelKey, socialShownPostSource } from './socialFormat'
import { useSocialItemImage } from './useSocialItemImage'

/**
 * Frozen (plan D8). Every addition is optional, so `SocialTrackerDrawer`
 * keeps rendering tiles unchanged; the Topic page dims tiles through
 * `useTopicHighlight`.
 */
interface SocialPostTileProps {
  post: SocialPostItem
  /** Names of the themes the post was tagged with. */
  topicNames?: Array<string>
  /** Name of the first brand tagged on the post, shown as a chip. */
  brandName?: string | null
  /** The chip's brand is seen in the image or video, not named: the eye chip instead of the swatch. */
  isBrandSeen?: boolean
  onOpen: (postToken: string) => void
  /** Quick action "Ask about this post"; absent hides it. */
  onAsk?: (post: SocialPostItem) => void
  /** Quick action "Hide post"; absent hides it (no permission, the tracker drawer). */
  onHide?: (post: SocialPostItem) => void
  /** The Topic the hide acts on, for the icon's "Hide from <Topic>"; absent says "Hide post". */
  hideTopicName?: string
  /** Defaults to `social-post-<postToken>`. */
  testId?: string
  /** The named brand's swatch (`socialBrandSwatchClass`); defaults to the first brand color. */
  brandSwatchClass?: string
  /** The named brand's stored picture (`SocialBrand.logoPath`), shown instead of the swatch. */
  brandLogoPath?: string | null
  /** The board's lead post in its order: a "Top post" chip and an accent ring. */
  isTop?: boolean
  /**
   * The Topic's period start (D23). A post published before it is earlier
   * context, outside every count: its date reads as the full date, muted.
   */
  periodFrom?: string
}

/** Published before the Topic's period: listed as context, never counted (D23). */
export function isBeforeSocialPeriod(
  publishedAt: string | null | undefined,
  periodFrom: string | undefined,
): boolean {
  if (!publishedAt || !periodFrom) {
    return false
  }
  return Date.parse(publishedAt) < Date.parse(periodFrom)
}

const RELATIVE_UNITS: Array<{ unit: Intl.RelativeTimeFormatUnit; seconds: number }> = [
  { unit: 'day', seconds: 86400 },
  { unit: 'hour', seconds: 3600 },
  { unit: 'minute', seconds: 60 },
]

/**
 * "3 days ago", "yesterday", "5 hours ago" within the last 30 days; the
 * short date beyond that (or for a date in the future). Null without a date.
 */
export function formatSocialRelativeDate(
  value: string | null | undefined,
  uiLanguage: string,
  now: Date = new Date(),
): string | null {
  if (!value) {
    return null
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return null
  }
  const elapsed = (now.getTime() - date.getTime()) / 1000
  if (elapsed < 0 || elapsed >= 30 * 86400) {
    return formatSocialDate(value, uiLanguage)
  }
  const formatter = new Intl.RelativeTimeFormat(intlLocaleForUi(uiLanguage), { numeric: 'auto' })
  for (const { unit, seconds } of RELATIVE_UNITS) {
    if (elapsed >= seconds) {
      return formatter.format(-Math.floor(elapsed / seconds), unit)
    }
  }
  return formatter.format(0, 'minute')
}

/** A count as the boards read it: compact with one decimal (606.3K, 3.6M). */
export function formatSocialMeasure(value: number, uiLanguage: string): string {
  return new Intl.NumberFormat(intlLocaleForUi(uiLanguage), {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value)
}

/** Dot color per sentiment: green and red carry meaning, mixed and neutral stay hueless (concept §4). */
export const SOCIAL_SENTIMENT_DOT_CLASS: Record<SocialSentiment, string> = {
  positive: 'bg-chart-positive',
  neutral: 'bg-foreground/20',
  negative: 'bg-chart-negative',
  mixed: 'bg-foreground/50',
}

/** Outline of the sentiment pill: the dot's color at a lower alpha. */
export const SOCIAL_SENTIMENT_PILL_BORDER_CLASS: Record<SocialSentiment, string> = {
  positive: 'border-chart-positive/40',
  neutral: 'border-foreground/15',
  negative: 'border-chart-negative/40',
  mixed: 'border-foreground/25',
}

/** Brand swatches by the brand's position in the Topic: chart tokens only, so an org brand restyles them. */
export const SOCIAL_BRAND_SWATCH_CLASSES: Array<string> = [
  'bg-chart-primary',
  'bg-chart-tertiary',
  'bg-chart-accent',
  'bg-chart-quinary',
  'bg-chart-senary',
]

export function socialBrandSwatchClass(index: number): string {
  const safe = index >= 0 ? index : 0
  return SOCIAL_BRAND_SWATCH_CLASSES[safe % SOCIAL_BRAND_SWATCH_CLASSES.length]
}

/** Two letters for an author's avatar: the first letters of two words, else the first two letters. */
export function socialAuthorInitials(name: string | null | undefined): string {
  const words = (name ?? '')
    .replace(/^@/, '')
    .split(/[\s._-]+/)
    .filter(Boolean)
  if (words.length === 0) {
    return '?'
  }
  if (words.length === 1) {
    return Array.from(words[0]).slice(0, 2).join('').toUpperCase()
  }
  return `${Array.from(words[0])[0]}${Array.from(words[1])[0]}`.toUpperCase()
}

interface SocialSentimentPillProps {
  sentiment: SocialSentiment
  testId?: string
  className?: string
}

/** An outlined pill with a colored dot and the label, so color is never alone. */
export function SocialSentimentPill(props: SocialSentimentPillProps) {
  const { sentiment, testId, className } = props

  const { t } = useTranslation('app')

  return (
    <span
      className={cn(
        'inline-flex h-5 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-2 text-[12px] font-medium text-foreground',
        SOCIAL_SENTIMENT_PILL_BORDER_CLASS[sentiment],
        className,
      )}
      data-testid={testId}
      data-sentiment={sentiment}
    >
      <span
        className={cn('h-1.5 w-1.5 rounded-full', SOCIAL_SENTIMENT_DOT_CLASS[sentiment])}
        aria-hidden="true"
      />
      {t(`socialListening.sentiment.${sentiment}`)}
    </span>
  )
}

interface SocialSourcePillProps {
  /** A Topic post carries `source`; a tracker post has none and shows no pill. */
  post: Pick<SocialPostItem, 'platform'> & { source?: SocialPostSource | null }
  testId?: string
}

/**
 * Organic, Brand-owned or Sponsored, as the judge labelled the post; nothing
 * when the source is unknown or the label is not available on its platform.
 */
export function SocialSourcePill(props: SocialSourcePillProps) {
  const { post, testId } = props

  const { t } = useTranslation('app')

  const source = socialShownPostSource(post.platform, post.source)
  if (!source) {
    return null
  }
  return (
    <span
      className="inline-flex h-5 shrink-0 items-center whitespace-nowrap rounded-full border border-border px-2 text-[12px] font-medium text-foreground"
      data-testid={testId}
      data-source={source}
    >
      {t(`socialListening.postSource.${source}`)}
    </span>
  )
}

interface SocialMarketChipProps {
  post: Pick<SocialTopicPostItem, 'token' | 'marketFit' | 'marketReason' | 'marketCountry' | 'language'>
}

/**
 * A pin with the country that placed the post (icon alone when nothing
 * named one), and why on hover or focus ("Account based in Brazil"). Nothing
 * on an unplaced post, on a language fit, on a Topic with no market and no
 * languages, or from a server without market fit.
 */
export function SocialMarketChip(props: SocialMarketChipProps) {
  const { post } = props

  const { t, i18n } = useTranslation('app')

  const reason = socialMarketReasonText(post, t, i18n.language)
  // Only a verdict that placed the post earns the pin: an unknown post and a language fit hide nothing.
  if (!reason || post.marketFit === 'unknown' || post.marketReason === 'language') {
    return null
  }
  const country = socialMarketCountryLabel(post, i18n.language)
  const testId = `social-post-market-reason-${post.token}`

  return (
    <span className="relative z-[2] inline-flex" onClick={(event) => event.stopPropagation()}>
      <SocialReasonTooltip reason={reason} isDisabled={false} isFocusable testId={testId}>
        <span
          className={cn(
            'inline-flex h-5 max-w-36 items-center gap-1 rounded-full text-[12px] text-muted-foreground',
          )}
          data-testid={testId}
          data-market-fit={post.marketFit}
          data-market-reason={post.marketReason}
        >
          <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {country ? <span className="truncate">{country}</span> : null}
        </span>
      </SocialReasonTooltip>
    </span>
  )
}

interface SocialAuthorAvatarProps {
  name: string
  platform: SocialPostItem['platform']
  /** The creator's stored picture (`authorAvatarPath`); absent or failing shows the initials. */
  avatarPath?: string | null
  /** Overrides the initials built from `name`. */
  initials?: string
  /** Shows the platform mark as a corner badge. */
  hasMark?: boolean
  size?: 'xs' | 'sm'
  /** Defaults to `social-author-avatar-img`. */
  testId?: string
}

const SOCIAL_AUTHOR_AVATAR_SIZE_CLASS: Record<'xs' | 'sm', string> = {
  xs: 'h-7 w-7',
  sm: 'h-9 w-9',
}

/**
 * The creator's picture when one is stored, else the theme-tinted initials
 * (also when the picture fails to load), with the platform mark as a corner
 * badge when asked.
 */
export function SocialAuthorAvatar(props: SocialAuthorAvatarProps) {
  const { name, platform, avatarPath = null, initials, hasMark = false, size = 'xs', testId } = props

  const [failedSrc, setFailedSrc] = useState<string | null>(null)

  const { src } = useSocialItemImage(avatarPath)

  const onImageError = () => {
    setFailedSrc(src)
  }

  const isPictureShown = Boolean(src) && src !== failedSrc
  const picture = isPictureShown ? (
    <img
      src={src as string}
      alt=""
      className={cn('shrink-0 rounded-full object-cover', SOCIAL_AUTHOR_AVATAR_SIZE_CLASS[size])}
      onError={onImageError}
      data-testid={testId ?? 'social-author-avatar-img'}
    />
  ) : (
    <UserAvatar initials={initials ?? socialAuthorInitials(name)} name={name} size={size} />
  )

  return (
    <span className="relative inline-flex shrink-0">
      {picture}
      {hasMark ? (
        <span className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-background">
          <SocialPlatformIcon platform={platform} className="h-2.5 w-2.5" />
        </span>
      ) : null}
    </span>
  )
}

const QUICK_ACTION_REVEAL_CLASS =
  '-translate-y-1 opacity-0 duration-150 group-focus-within:translate-y-0 group-focus-within:opacity-100 group-hover:translate-y-0 group-hover:opacity-100 motion-reduce:transition-none'
/**
 * Below tablet width or without a hover nothing reveals on hover, so the hide
 * icon stays shown. Written as a raw query: the `tall` raw screen turns off
 * Tailwind's `max-*` variants.
 */
const QUICK_ACTION_ALWAYS_SHOWN_CLASS =
  '[@media(max-width:767px)]:translate-y-0 [@media(max-width:767px)]:opacity-100 [@media(hover:none)]:translate-y-0 [@media(hover:none)]:opacity-100'
/** A chip on the media's corner moves left of the always shown hide icon. */
const HIDE_ICON_CHIP_OFFSET_CLASS = '[@media(max-width:767px)]:right-11 [@media(hover:none)]:right-11'
/** A text tile's author row starts level with the icon: its sentiment pill stays clear of it. */
const HIDE_ICON_ROW_OFFSET_CLASS = '[@media(max-width:767px)]:pr-9 [@media(hover:none)]:pr-9'

/** The aspect of a tile's media: YouTube is landscape, the rest portrait, a lone placeholder 4:3. */
function mediaAspectClass(platform: SocialPostItem['platform'], isPlaceholderOnly: boolean): string {
  if (isPlaceholderOnly) {
    return 'aspect-[4/3]'
  }
  return platform === 'youtube' ? 'aspect-[16/10]' : 'aspect-[4/5]'
}

/**
 * One post on a board (concept §5.10): media with its chips, the author row
 * with the sentiment pill, a 3-line excerpt, then reach first, likes,
 * comments, date and the brand chip. A post whose still is missing gets a
 * 4:3 typographic placeholder on a primary tint; a post with no media at all
 * is a text tile. Surfaces are the background with a hairline, never grey.
 */
export function SocialPostTile(props: SocialPostTileProps) {
  const {
    post,
    topicNames = [],
    brandName = null,
    isBrandSeen = false,
    onOpen,
    onAsk,
    onHide,
    hideTopicName,
    testId,
    brandSwatchClass,
    brandLogoPath = null,
    isTop = false,
    periodFrom,
  } = props

  const { t, i18n } = useTranslation('app')
  const [isImageLoaded, setIsImageLoaded] = useState(false)
  const storedImage = useSocialItemImage(post.images?.[0]?.imagePath ?? null)
  const highlight = useTopicHighlight()
  // A post repeats across theme and brand boards: each tile is its own player.
  const tileId = useId()
  const player = useSocialPlayingPost(post.token, `tile${tileId}`)

  const platformLabel = SOCIAL_PLATFORM_LABELS[post.platform]
  const handle = post.authorHandle ? `@${post.authorHandle}` : null
  const displayName = post.providerMeta?.displayName ?? null
  const author = handle ?? t('socialListening.detail.unknownAuthor')
  const name = displayName ?? handle ?? platformLabel
  const followers = post.providerMeta?.followers ?? null
  // The name line already shows the handle when there is no display name: never twice.
  const subHandle = displayName ? handle : null
  const sub = [
    ...(subHandle ? [subHandle] : []),
    ...(followers != null
      ? [
          t('socialListening.topicPost.followers', {
            count: followers,
            value: formatSocialMeasure(followers, i18n.language),
          }),
        ]
      : []),
  ].join(', ')
  const publishedAt = post.publishedAt ?? post.collectedAt
  const absoluteDate = formatSocialDate(publishedAt, i18n.language)
  const isEarlier = isBeforeSocialPeriod(post.publishedAt, periodFrom)
  const date = isEarlier ? absoluteDate : formatSocialRelativeDate(publishedAt, i18n.language)
  // Stored stills only: raw provider CDN links expire (403) and are never shown.
  const hasStoredImages = (post.images?.length ?? 0) > 0
  const imageSrc = storedImage.src
  const hasMediaSource = hasStoredImages
  const isVideo = post.media.hasVideo
  // A video plays through its platform's official player, mounted only after
  // a click on Play (plan D3, D4): never a raw provider CDN link.
  const playerUrl = isVideo ? socialPlayerUrl(post) : null
  const isPlayerShown = player.isPlaying && playerUrl != null
  const isTextTile = !hasMediaSource && playerUrl == null
  const isPlaceholderOnly = hasMediaSource && imageSrc == null && !storedImage.isLoading
  const photoCount = post.media.imageCount
  const isCarousel = !isVideo && hasStoredImages && photoCount > 1
  const { likes, comments, views } = post.engagement
  const sentiment = post.sentiment ?? null
  const label = sentiment
    ? t('socialListening.topicPage.tile.openAria', {
        author,
        platform: platformLabel,
        sentiment: t(`socialListening.sentiment.${sentiment}`),
      })
    : t('socialListening.topicView.tileAria', { author, platform: platformLabel })
  // The tracker drawer has no highlight provider, so nothing dims there.
  const isDimmed = highlight.isDimmed(post as SocialTopicPostItem)
  const isLit = highlight.active != null && highlight.active.kind !== 'post' && !isDimmed
  const isThisPostActive =
    highlight.active?.kind === 'post' && highlight.active.postToken === post.token

  const hideLabel = hideTopicName
    ? t('socialListening.topicPost.hide', { name: hideTopicName })
    : t('socialListening.topicPage.tile.hide')
  const chipOffsetClass = onHide ? HIDE_ICON_CHIP_OFFSET_CLASS : undefined

  function stop(event: MouseEvent) {
    event.stopPropagation()
  }

  function onPointerEnter() {
    if (!isThisPostActive) {
      highlight.setActive({ kind: 'post', postToken: post.token })
    }
  }

  function onPointerLeave() {
    if (isThisPostActive) {
      highlight.setActive(null)
    }
  }

  // No still (loading, expired or never stored): the caption is the cover,
  // set like a pull quote on the platform tint. Never a fake video frame.
  const placeholder = (
    <span
      aria-hidden={isPlaceholderOnly ? undefined : true}
      className="absolute inset-0 flex flex-col justify-end overflow-hidden bg-[linear-gradient(160deg,hsl(var(--primary)/0.22)_0%,hsl(var(--primary)/0.06)_55%,hsl(var(--background))_100%)] p-4 pt-11"
      data-testid={`social-post-placeholder-${post.token}`}
    >
      <SocialPlatformIcon
        platform={post.platform}
        className="pointer-events-none absolute -bottom-6 -right-5 h-28 w-28 rotate-[-10deg] text-primary opacity-[0.09]"
      />
      {isPlaceholderOnly ? (
        <>
          <Quote className="mb-1.5 h-5 w-5 shrink-0 text-primary/70" aria-hidden="true" />
          <span className="relative line-clamp-5 font-display text-[19px] leading-[1.22] tracking-[-0.005em] text-foreground">
            {post.text.slice(0, 180)}
          </span>
        </>
      ) : null}
    </span>
  )

  const playButton = playerUrl ? (
    <button
      type="button"
      aria-label={t('socialListening.topicPage.tile.play')}
      title={t('socialListening.topicPage.tile.play')}
      className={cn(SOCIAL_PLAY_BUTTON_CLASS, 'h-9 w-9')}
      onClick={(event) => {
        stop(event)
        player.play()
      }}
      data-testid={`social-post-tile-play-${post.token}`}
    >
      <Play className="ml-0.5 h-3.5 w-3.5 fill-current" aria-hidden="true" />
    </button>
  ) : null

  const platformChip = (
    <span className="absolute left-2.5 top-2.5 z-[2] inline-flex h-6 items-center gap-1.5 rounded-full bg-background/90 px-2 text-[12px] font-medium text-foreground shadow-sm backdrop-blur-md">
      <SocialPlatformIcon platform={post.platform} className="h-3 w-3" />
      {platformLabel}
    </span>
  )

  // Playing: the platform's player in the media frame, with its close control.
  const playerMedia = isPlayerShown ? (
    <div
      className={cn('relative z-[2] overflow-hidden bg-background', socialPlayerAspectClass(post.platform))}
      onClick={stop}
      data-testid={`social-post-player-${post.token}`}
    >
      <iframe
        src={playerUrl}
        title={t('socialListening.detail.previewTitle', { author })}
        allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
        className="absolute inset-0 h-full w-full border-0"
        data-testid={`social-post-embed-${post.token}`}
      />
      <button
        type="button"
        aria-label={t('socialListening.topicPage.tile.closePlayer')}
        title={t('socialListening.topicPage.tile.closePlayer')}
        className={SOCIAL_PLAYER_CLOSE_CLASS}
        onClick={(event) => {
          stop(event)
          player.stop()
        }}
        data-testid={`social-post-tile-close-${post.token}`}
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </div>
  ) : null

  // A video with no stored cover: a neutral frame with Play, nothing loaded yet.
  const videoFrame =
    !hasMediaSource && playerUrl ? (
      <div
        className={cn(
          'relative overflow-hidden bg-[linear-gradient(160deg,hsl(var(--primary)/0.18)_0%,hsl(var(--primary)/0.05)_55%,hsl(var(--background))_100%)]',
          mediaAspectClass(post.platform, false),
        )}
        data-testid={`social-post-video-frame-${post.token}`}
      >
        <SocialPlatformIcon
          platform={post.platform}
          className="pointer-events-none absolute -bottom-6 -right-5 h-28 w-28 rotate-[-10deg] text-primary opacity-[0.09]"
        />
        {platformChip}
        {playButton}
      </div>
    ) : null

  const media = isTextTile ? null : playerMedia ?? videoFrame ?? (
    <div
      className={cn(
        'relative overflow-hidden bg-primary/5',
        mediaAspectClass(post.platform, isPlaceholderOnly),
      )}
    >
      {isImageLoaded ? null : placeholder}
      {imageSrc ? (
        <img
          src={imageSrc}
          alt=""
          loading="lazy"
          decoding="async"
          onLoad={() => setIsImageLoaded(true)}
          className={cn(
            'absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-[1.04] motion-reduce:transition-none',
            isImageLoaded ? 'opacity-100' : 'opacity-0',
          )}
          data-testid={`social-post-image-${post.token}`}
        />
      ) : null}
      {platformChip}
      {isCarousel ? (
        <span
          className={cn(
            'absolute right-2.5 top-2.5 z-[2] inline-flex h-6 items-center gap-1 rounded-full bg-background/90 px-2 text-[12px] font-medium tabular-nums text-foreground shadow-sm backdrop-blur-md transition-opacity group-hover:opacity-0',
            chipOffsetClass,
          )}
          aria-label={t('socialListening.topicPage.tile.photos', { count: photoCount })}
          title={t('socialListening.topicPage.tile.photos', { count: photoCount })}
          data-testid={`social-post-photo-count-${post.token}`}
        >
          <Layers className="h-3 w-3" aria-hidden="true" />
          {photoCount}
        </span>
      ) : null}
      {playButton}
      {isVideo ? (
        <span
          className={cn(
            'absolute right-2.5 top-2.5 z-[2] inline-flex h-6 items-center gap-1 rounded-full bg-background/90 px-2 text-[12px] font-medium text-foreground shadow-sm backdrop-blur-md transition-opacity group-hover:opacity-0',
            chipOffsetClass,
          )}
        >
          <Play className="h-2.5 w-2.5 fill-current" aria-hidden="true" />
          {t('socialListening.topicPage.tile.video')}
        </span>
      ) : null}
    </div>
  )

  const quickActionClass =
    'flex h-7 w-7 items-center justify-center rounded-full bg-popover text-popover-foreground shadow-sm transition hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
  const quickActions = (
    <div className="absolute right-2.5 top-2.5 z-[3] flex gap-1">
      <a
        href={post.url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t('socialListening.topicPage.tile.openOriginal')}
        title={t('socialListening.topicPage.tile.openOriginal')}
        className={cn(quickActionClass, QUICK_ACTION_REVEAL_CLASS)}
        onClick={stop}
        data-testid={`social-post-open-original-${post.token}`}
      >
        <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
      </a>
      {onAsk ? (
        <button
          type="button"
          aria-label={t('socialListening.topicPage.tile.ask')}
          title={t('socialListening.topicPage.tile.ask')}
          className={cn(quickActionClass, QUICK_ACTION_REVEAL_CLASS)}
          onClick={(event) => {
            stop(event)
            onAsk(post)
          }}
          data-testid={`social-post-ask-${post.token}`}
        >
          <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      ) : null}
      {onHide ? (
        <button
          type="button"
          aria-label={hideLabel}
          title={hideLabel}
          className={cn(quickActionClass, QUICK_ACTION_REVEAL_CLASS, QUICK_ACTION_ALWAYS_SHOWN_CLASS)}
          onClick={(event) => {
            stop(event)
            onHide(post)
          }}
          data-testid={`social-post-tile-hide-${post.token}`}
        >
          <EyeOff className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  )

  const brandChip = brandName ? (
    <span
      className="ml-auto inline-flex h-5 max-w-full items-center gap-1.5 rounded-full border border-border px-2 text-[12px] text-foreground"
      title={
        isBrandSeen
          ? t('socialListening.topicPage.tile.seenInImage')
          : t('socialListening.topicPage.tile.namedInPost')
      }
      data-testid={`social-post-brand-${post.token}`}
      data-seen={isBrandSeen ? 'true' : 'false'}
    >
      {isBrandSeen ? (
        <Eye
          className="h-3 w-3 shrink-0"
          aria-hidden="true"
          data-testid={`social-post-brand-seen-${post.token}`}
        />
      ) : (
        <SocialBrandMark
          logoPath={brandLogoPath}
          swatch={
            <span
              className={cn('h-2 w-2 shrink-0 rounded-sm', brandSwatchClass ?? socialBrandSwatchClass(0))}
              aria-hidden="true"
            />
          }
          testId={`social-post-brand-mark-${post.token}`}
        />
      )}
      <span className="truncate">{brandName}</span>
    </span>
  ) : null

  return (
    <article
      className={cn(
        'group relative mb-3 w-full break-inside-avoid overflow-hidden rounded-[calc(var(--radius)+4px)] border border-card-border bg-background text-left transition duration-200 animate-in fade-in-0 slide-in-from-bottom-2 hover:-translate-y-0.5 hover:border-foreground/20 hover:shadow-lg motion-reduce:transform-none motion-reduce:animate-none motion-reduce:transition-none',
        isDimmed && 'opacity-[0.28] saturate-[0.3]',
        isLit && 'ring-2 ring-ring',
        isTop && !isLit && 'border-primary/40 shadow-[0_0_0_1px_hsl(var(--primary)/0.25)]',
      )}
      onClick={() => {
        if (player.isPlaying) {
          player.stop()
        }
        onOpen(post.token)
      }}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      data-testid={testId ?? `social-post-${post.token}`}
      data-board-post={post.token}
      data-dimmed={isDimmed ? 'true' : undefined}
      data-top={isTop ? 'true' : undefined}
    >
      <button
        type="button"
        aria-label={label}
        className="absolute inset-0 z-[1] rounded-[inherit] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        data-testid={`social-post-open-${post.token}`}
      />
      {media}
      {quickActions}
      <div className="px-3 pb-3 pt-2.5">
        {isTop ? (
          <span
            className="mb-2 inline-flex h-5 items-center gap-1 rounded-full bg-primary/10 px-2 text-[12px] font-medium text-foreground"
            data-testid={`social-post-top-${post.token}`}
          >
            <TrendingUp className="h-3 w-3 text-primary" aria-hidden="true" />
            {t('socialListening.topicPage.tile.topPost')}
          </span>
        ) : null}
        <div
          className={cn(
            'grid grid-cols-[28px_minmax(0,1fr)_auto] items-center gap-2',
            isTextTile && !isTop && onHide && HIDE_ICON_ROW_OFFSET_CLASS,
          )}
          data-testid={`social-post-author-row-${post.token}`}
        >
          <SocialAuthorAvatar
            name={name}
            platform={post.platform}
            avatarPath={post.authorAvatarPath}
            hasMark={isTextTile}
            testId={`social-post-avatar-img-${post.token}`}
          />
          <span className="min-w-0">
            <span className="block truncate text-[12px] font-semibold text-foreground">{name}</span>
            {sub ? (
              <span className="block truncate text-[12px] tabular-nums text-muted-foreground">
                {sub}
              </span>
            ) : null}
          </span>
          {sentiment ? (
            <SocialSentimentPill sentiment={sentiment} testId={`social-post-sentiment-${post.token}`} />
          ) : (
            <span />
          )}
        </div>
        {isPlaceholderOnly ? null : (
          <p
            className={cn(
              'mt-2 whitespace-pre-line text-[13px] leading-[1.45] text-foreground',
              isTextTile ? 'line-clamp-[7]' : 'line-clamp-2',
            )}
          >
            {post.text}
          </p>
        )}
        <div
          className="mt-2.5 flex flex-wrap items-center gap-x-2.5 gap-y-1.5 text-[12px] tabular-nums text-muted-foreground"
          data-testid={`social-post-metrics-${post.token}`}
        >
          {views != null ? (
            <span
              className="inline-flex items-center gap-1 font-semibold text-foreground"
              aria-label={t('socialListening.topicPage.tile.viewsAria', {
                value: formatSocialMeasure(views, i18n.language),
              })}
              data-testid={`social-post-views-${post.token}`}
            >
              <Eye className="h-3.5 w-3.5" aria-hidden="true" />
              {formatSocialMeasure(views, i18n.language)}
            </span>
          ) : null}
          {likes != null ? (
            <span
              className="inline-flex items-center gap-1"
              data-testid={`social-post-likes-${post.token}`}
              title={t(`socialListening.topicPage.sheet.${socialMetricLabelKey(post.platform, 'likes')}`)}
            >
              {post.platform === 'reddit' ? (
                <ArrowUp className="h-3.5 w-3.5" aria-hidden="true" />
              ) : (
                <Heart className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              {formatSocialMeasure(likes, i18n.language)}
            </span>
          ) : null}
          {comments != null ? (
            <span className="inline-flex items-center gap-1">
              <MessageCircle className="h-3.5 w-3.5" aria-hidden="true" />
              {formatSocialMeasure(comments, i18n.language)}
            </span>
          ) : null}
          {date ? (
            <time
              dateTime={publishedAt ?? undefined}
              title={absoluteDate ?? undefined}
              className={isEarlier ? 'italic opacity-70' : undefined}
              data-period={isEarlier ? 'earlier' : undefined}
              data-testid={`social-post-date-${post.token}`}
            >
              {date}
            </time>
          ) : null}
          <SocialMarketChip post={post as SocialTopicPostItem} />
          <SocialSourcePill
            post={post}
            testId={`social-post-source-${post.token}`}
          />
          {topicNames.length > 0 ? (
            <span className="flex flex-wrap gap-1" data-testid="social-post-topics">
              {topicNames.map((topicName) => (
                <span
                  key={topicName}
                  className="max-w-full truncate rounded-full border border-border px-2 text-[12px] leading-5 text-foreground"
                >
                  {topicName}
                </span>
              ))}
            </span>
          ) : null}
          {brandChip}
        </div>
      </div>
    </article>
  )
}
